import React, { FC, useState } from 'react';
import styled, { css } from 'styled-components';
import { Flexbox, FlexboxCenter, FlexboxVertical } from '../../Layout';
import { Colors } from '../../../../styles/colors';
import { Icon } from '../../Icon';
import { Headline4, Headline6 } from '../../Text';
import { NoteItem } from './NoteItem';
import { NoteInputBox } from './NoteInputBox';
import { useRecoilState } from 'recoil';
import { NotesPopupState, NotesState } from '../../../../state/NotesState';
import { Note } from '../../../../interfaces/Operation';
import { useShowConfirmationPopup } from '../../ConfirmationPopup';

const HoverEffect = css`
  cursor: pointer;
  transition: color 150ms ease-out;
  
  &:hover {
    @media (pointer: fine) {
      color: ${Colors.tertiary};
    }
  }
`;

const NoteIconWrapper = styled(FlexboxCenter)<{ opened?: boolean }>`
  position: fixed;
  top: ${({ opened = false }) => opened ? 'calc(100% - 4.5rem)' : '67%'};
  right: 0;
  
  transition: top 300ms ease-out;
  
  width: 4.5rem;
  height: 4.5rem;
  background: ${Colors.grayDark};
  color: ${Colors.white};
  
  ${HoverEffect}
`;

const PopupWrapper = styled(FlexboxVertical)<{ opened?: boolean }>`
  position: fixed;
  top: 16rem;
  left: 4.5rem;
  right: 4.5rem;
  bottom: 0;
  
  z-index: 5;
  pointer-events: all;
  
  background: ${Colors.white};
  padding: 3rem 4rem;
  overflow: auto;
  
  transition: transform 300ms ease-out;
  transform: ${({ opened = false }) => opened ? 'none' : 'translateY(100%)'};
  
  ${Headline6} {
    margin-top: 5rem;
    margin-bottom: 1.5rem;
  }
`;

const IconWrapper = styled(FlexboxCenter)`
  ${HoverEffect}
`;
const NotesHead = styled(Flexbox)`
  margin-bottom: 3.5rem;

  ${Headline4} {
    flex: 1;
    padding-right: 1rem;
  }
`;

const ScrollWrapper = styled(FlexboxVertical)`
  overflow: auto;
  flex: 1;
  padding-bottom: 3.5rem;
`;

const GridLayout = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0 3rem;
`;

interface Props {
  preselectedTitle?: string;
}
export const NotesPopup: FC<Props> = ({ ...props }) => {
  const [notes, setNotes] = useRecoilState(NotesState);
  const [notesPopupState, setNotesPopupState] = useRecoilState(NotesPopupState);

  const showConfirmationPopup = useShowConfirmationPopup();

  const open = notesPopupState.open;
  const setOpen = (open: boolean) => {
    setNotesPopupState({
      ...notesPopupState,
      open,
    });
  }
  const editNote = notesPopupState.editNode;
  const setEditNote = (editNode: Note) => {
    setNotesPopupState({
      ...notesPopupState,
      editNode,
    });
  }

  return (
    <>
    <NoteIconWrapper opened={open} onClick={() => setOpen(!open)}>
      <Icon icon={'note'} size={44} />
    </NoteIconWrapper>
    <PopupWrapper opened={open}>
      <NotesHead>
        <Headline4>Notizen</Headline4>
        <IconWrapper onClick={() => setOpen(false)}>
          <Icon icon={'close'} size={24} />
        </IconWrapper>
      </NotesHead>

      <ScrollWrapper>
        {
          editNote
            ?
            <NoteInputBox
              note={editNote}
              onSaveClick={(note) => {
                const nextNote = {
                  ...note,
                  timestamp: note.timestamp || new Date().toISOString(),
                }
                const nextNotes = [...notes.list];
                const pos = nextNotes.findIndex(({ timestamp }) => timestamp === nextNote.timestamp);
                if (pos < 0) {
                  nextNotes.push(nextNote);
                } else {
                  nextNotes.splice(pos, 1, nextNote);
                }
                setNotes({ list: nextNotes });
                setEditNote(null);
              }}
              onCancelClick={() => setEditNote(null)}
            />
            : <GridLayout><NoteItem onEditClick={() => setEditNote({ subject: props.preselectedTitle || '', body: '', timestamp: new Date().toISOString() })} title={'neue Notiz hinzufügen'} /></GridLayout>
        }

        <Headline6>Bereits vorhandene Notizen</Headline6>
        <GridLayout>
          <FlexboxVertical>
            {
              notes.list.map((note) => (
                <NoteItem
                  title={note.subject}
                  onEditClick={() => setEditNote(note)}
                  onDeleteClick={() => {
                    showConfirmationPopup({
                      open: true,
                      actionButtonLabel: 'Löschen',
                      closeFn() {
                        showConfirmationPopup(null);
                      },
                      actionFn() {
                        showConfirmationPopup(null);    // close popup
                        setNotes({ list: notes.list.filter(({ timestamp }) => timestamp !== note.timestamp) });
                      },
                      renderer() {
                        return <>Wollen Sie die Notiz zu "{ note.subject }" wirklich löschen?</>;
                      },
                    });
                  }}
                />
              ))
            }
          </FlexboxVertical>
        </GridLayout>
      </ScrollWrapper>

    </PopupWrapper>
    </>
  )
}
